import { useApolloClient } from '@apollo/client';
import { first, isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FileUpload } from '@shared/components/blocks/types';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { DEFAULT_MIME_TYPE, uploadFile } from '@shared/scenes/upload/store/actions';
import { BLOCKED_CONTENT_TYPES, MBUploadUtil, MBUploadUtilFile } from '@shared/scenes/upload/util';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';

export interface FileUploadHook {
  handleUploadFile(file: MBUploadUtilFile): void;
  handleDrop(file: File[]): void;
  handleClear(file: FileUpload): void;
  showFilePicker(): void;
  uploading: boolean;
}

export interface FileUploadHookInput {
  onFileAdded(file: FileUpload): void;
  onFileRemoved(file: FileUpload): void;
  acceptedContentTypes?: string[];
}

export const useFileUpload: (input: FileUploadHookInput) => FileUploadHook = ({
  onFileAdded,
  onFileRemoved,
  acceptedContentTypes,
}) => {
  const client = useApolloClient();
  const [uploading, setUploading] = useState(false);

  const validateUploads = useValidateUploads({
    showUpgradeModal: true,
  });

  const handleUploadFile = useCallback(
    async (file: MBUploadUtilFile) => {
      setUploading(true);

      try {
        const isFileValid = !!validateUploads([file]);
        if (!isFileValid) {
          return;
        }

        const url = await uploadFile(client, file);
        onFileAdded({
          url,
          name: file.name,
          contentType: file.contentType || DEFAULT_MIME_TYPE,
          width: file.width,
          height: file.height,
        });
      } catch (err) {
        console.error(err);
        MBAlert.alert({
          message: i18n('card.blocks.image.uploadFailed'),
        });
      } finally {
        setUploading(false);
      }
    },
    [client, onFileAdded, validateUploads],
  );

  const showFilePicker = useCallback(async () => {
    const files = await MBUploadUtil.selectFiles({ multiple: false, acceptedContentTypes });
    const firstFile = first(files);
    if (!isNil(firstFile)) {
      handleUploadFile(firstFile);
    }
  }, [acceptedContentTypes, handleUploadFile]);

  const handleClear = useCallback((file: FileUpload) => onFileRemoved(file), [onFileRemoved]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      const safeFiles = files?.filter(f => !BLOCKED_CONTENT_TYPES.includes(f.type));
      if (safeFiles?.length) {
        const dims = await MBUploadUtil.getWebFileDimensions(safeFiles[0]);

        handleUploadFile({
          id: String(Date.now()),
          url: URL.createObjectURL(safeFiles[0]),
          name: safeFiles[0].name,
          contentType: safeFiles[0].type,
          size: safeFiles[0].size,
          ...(dims ?? {}),
        });
      }
    },
    [handleUploadFile],
  );

  const value = useMemo<FileUploadHook>(
    () => ({
      uploading,
      handleUploadFile,
      handleDrop,
      handleClear,
      showFilePicker,
    }),
    [uploading, handleUploadFile, handleDrop, handleClear, showFilePicker],
  );

  return value;
};
